import React, { useState, useEffect, useRef, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronRight, ChevronDown, ChevronUp, Play, PlayCircle, CheckCircle, Circle, FolderOpen, Video, Volume2, VolumeX, Maximize, Pause, SkipForward, Info, AlertTriangle, AlertCircle, ArrowLeft, Home, Layers, Film, Sparkles, RefreshCw, GraduationCap, Eye, Sun, Search, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  effectiveCommentaireTheme,
  commentBubbleFrameClasses,
  commentBubblePrimaryTextClass,
} from '@/lib/videoTutorialCommentTheme';
import { COMMENT_STYLES } from '@/lib/videoTutorialCommentStyles';
import TutorialStepStage, { stepHasUsableScreenshot } from '@/components/TutorialStepStage';
import { resolveTutorialFullViewOpenUrl } from '@/lib/tutorialFullViewUrl';
import { useVideoTutorial } from '@/context/VideoTutorialContext';

export function hasStepScreenshots(tutorial) {
  return tutorial?.commentaires?.some(
    (c) =>
      c.screenshot_url && typeof c.screenshot_url === 'string' &&
      (c.screenshot_url.startsWith('data:image') || c.screenshot_url.startsWith('/api/files/') || c.screenshot_url.startsWith('http'))
  );
}

export function isStepsLikeTutorial(tutorial) {
  if (!tutorial || tutorial.mode === 'lien') return false;
  return !!(tutorial.mode === 'etapes' || hasStepScreenshots(tutorial));
}

export function isLinkTutorial(tutorial) {
  return !!(tutorial && tutorial.mode === 'lien');
}

export function getTutorialExternalLink(tutorial) {
  if (!isLinkTutorial(tutorial)) return '';
  const raw = (tutorial.video_url || '').trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) return raw;
  return `https://${raw}`;
}

// Composant pour le lecteur YouTube avec commentaires interactifs
export const YouTubePlayerWithComments = ({ tutorial, onComplete, onClose }) => {
  const playerRef = useRef(null);
  const playerContainerId = useRef(`youtube-help-${Date.now()}`);
  const containerRef = useRef(null);
  const [playerReady, setPlayerReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [activeComment, setActiveComment] = useState(null);
  const [triggeredComments, setTriggeredComments] = useState(new Set());
  const [showControls, setShowControls] = useState(true);
  const timeUpdateInterval = useRef(null);
  const hideControlsTimeout = useRef(null);
  const playbackStart = tutorial?.playback_start != null ? Number(tutorial.playback_start) : null;
  const playbackEnd = tutorial?.playback_end != null ? Number(tutorial.playback_end) : null;
  const effectiveStart = playbackStart ?? 0;
  const commentBubbleTheme = effectiveCommentaireTheme(activeComment, tutorial);
  // Ne considérer que les commentaires dans la plage de lecture (début → fin)
  const comments = useMemo(() => {
    const list = tutorial?.commentaires || [];
    return list.filter((c) => {
      const t = c.timestamp;
      if (playbackStart != null && t < playbackStart) return false;
      if (playbackEnd != null && t > playbackEnd) return false;
      return true;
    });
  }, [tutorial?.commentaires, playbackStart, playbackEnd]);

  // Extraire l'ID YouTube
  const getYoutubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
    return match ? match[1] : null;
  };

  const youtubeId = getYoutubeId(tutorial?.video_url);

  // Initialiser le player YouTube
  useEffect(() => {
    if (!youtubeId) return;

    const loadYoutubeAPI = () => {
      return new Promise((resolve) => {
        if (window.YT && window.YT.Player) {
          resolve();
          return;
        }

        window.onYouTubeIframeAPIReady = () => {
          resolve();
        };

        if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
          const tag = document.createElement('script');
          tag.src = 'https://www.youtube.com/iframe_api';
          document.head.appendChild(tag);
        } else if (window.YT && window.YT.Player) {
          resolve();
        }
      });
    };

    const initPlayer = async () => {
      await loadYoutubeAPI();
      await new Promise(resolve => setTimeout(resolve, 100));

      const container = document.getElementById(playerContainerId.current);
      if (!container || playerRef.current) return;

      const playerVars = {
        autoplay: 0,
        controls: 0,
        modestbranding: 1,
        rel: 0,
        fs: 0,
        playsinline: 1,
        disablekb: 1,
      };
      if (playbackStart != null) playerVars.start = Math.floor(playbackStart);
      if (playbackEnd != null) playerVars.end = Math.floor(playbackEnd);

      playerRef.current = new window.YT.Player(playerContainerId.current, {
        videoId: youtubeId,
        width: '100%',
        height: '100%',
        playerVars,
        events: {
          onReady: (event) => {
            const fullDuration = event.target.getDuration();
            // Durée affichée = plage de lecture si définie, sinon durée complète
            const start = playbackStart ?? 0;
            if (playbackEnd != null) {
              setDuration(Math.min(fullDuration, playbackEnd) - start);
            } else {
              setDuration(fullDuration);
            }
            setPlayerReady(true);
            // Démarrer la lecture automatiquement (contrôles masqués en centre d'aide)
            event.target.playVideo();
          },
          onStateChange: (event) => {
            const playing = event.data === window.YT.PlayerState.PLAYING;
            setIsPlaying(playing);

            if (playing) {
              if (timeUpdateInterval.current) clearInterval(timeUpdateInterval.current);
              timeUpdateInterval.current = setInterval(() => {
                if (playerRef.current && playerRef.current.getCurrentTime) {
                  const time = playerRef.current.getCurrentTime();
                  setCurrentTime(time);
                  
                  // Vérifier si on atteint un commentaire (utiliser la ref pour la version la plus récente)
                  if (checkForCommentRef.current) {
                    checkForCommentRef.current(time);
                  }
                }
              }, 100);
            } else {
              if (timeUpdateInterval.current) {
                clearInterval(timeUpdateInterval.current);
              }
            }
            
            // Vidéo terminée
            if (event.data === window.YT.PlayerState.ENDED) {
              onComplete?.();
            }
          },
        },
      });
    };

    initPlayer();

    return () => {
      if (timeUpdateInterval.current) {
        clearInterval(timeUpdateInterval.current);
      }
      if (playerRef.current && playerRef.current.destroy) {
        try {
          playerRef.current.destroy();
        } catch (e) {}
        playerRef.current = null;
      }
    };
  }, [youtubeId]);

  // Ref pour stocker la dernière version de checkForComment
  const checkForCommentRef = useRef(null);

  // Vérifier si on doit afficher un commentaire
  const checkForComment = useCallback((time) => {
    if (activeComment) return;
    
    const comment = comments.find(c => 
      Math.abs(c.timestamp - time) < 0.3 && 
      !triggeredComments.has(c.id)
    );

    if (comment) {
      if (playerRef.current) {
        playerRef.current.pauseVideo();
      }
      setActiveComment(comment);
      setTriggeredComments(prev => new Set([...prev, comment.id]));
    }
  }, [comments, activeComment, triggeredComments]);

  // Mettre à jour la ref à chaque changement
  useEffect(() => {
    checkForCommentRef.current = checkForComment;
  }, [checkForComment]);

  // Continuer après un commentaire
  const continuePlayback = () => {
    setActiveComment(null);
    if (playerRef.current) {
      playerRef.current.playVideo();
    }
  };

  // Sauter le commentaire
  const skipComment = () => {
    setActiveComment(null);
    if (playerRef.current) {
      playerRef.current.seekTo(playerRef.current.getCurrentTime() + 0.5, true);
      playerRef.current.playVideo();
    }
  };

  // Contrôles
  const togglePlay = () => {
    if (!playerRef.current || !playerReady) return;
    if (isPlaying) {
      playerRef.current.pauseVideo();
    } else {
      playerRef.current.playVideo();
    }
  };

  const seekTo = (time) => {
    if (!playerRef.current || !playerReady) return;
    const clamped = playbackEnd != null
      ? Math.max(effectiveStart, Math.min(playbackEnd, time))
      : Math.max(effectiveStart, time);
    playerRef.current.seekTo(clamped, true);
    setCurrentTime(clamped);
    if (activeComment) {
      setActiveComment(null);
    }
    // Réinitialiser les commentaires passés
    setTriggeredComments(prev => {
      const newSet = new Set();
      prev.forEach(id => {
        const comment = comments.find(c => c.id === id);
        if (comment && comment.timestamp <= time) {
          newSet.add(id);
        }
      });
      return newSet;
    });
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Masquer les contrôles après inactivité
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const resetHideTimer = () => {
      setShowControls(true);
      if (hideControlsTimeout.current) {
        clearTimeout(hideControlsTimeout.current);
      }
      if (isPlaying && !activeComment) {
        hideControlsTimeout.current = setTimeout(() => {
          setShowControls(false);
        }, 3000);
      }
    };

    container.addEventListener('mousemove', resetHideTimer);
    container.addEventListener('click', resetHideTimer);

    return () => {
      container.removeEventListener('mousemove', resetHideTimer);
      container.removeEventListener('click', resetHideTimer);
      if (hideControlsTimeout.current) {
        clearTimeout(hideControlsTimeout.current);
      }
    };
  }, [isPlaying, activeComment]);

  // Calculer les positions des points d'ancrage (même formule que zone cible : dimensions stockées)
  const getAnchorPosition = useCallback((comment, anchorType, anchorKey) => {
    const ANCHOR_POINTS = {
      'top-left': { x: 0, y: 0 },
      'top': { x: 50, y: 0 },
      'top-right': { x: 100, y: 0 },
      'left': { x: 0, y: 50 },
      'center': { x: 50, y: 50 },
      'right': { x: 100, y: 50 },
      'bottom-left': { x: 0, y: 100 },
      'bottom': { x: 50, y: 100 },
      'bottom-right': { x: 100, y: 100 },
    };
    
    const anchor = ANCHOR_POINTS[anchorKey || 'center'];
    
    if (anchorType === 'comment') {
      // Même approche que zone cible : utiliser largeur/hauteur stockées dans le commentaire
      const widthPercent = comment.largeur ?? 18;
      const heightPercent = comment.hauteur ?? 10;
      const halfW = widthPercent / 2;
      const halfH = heightPercent / 2;
      return {
        x: comment.position_x - halfW + (widthPercent * anchor.x / 100),
        y: comment.position_y - halfH + (heightPercent * anchor.y / 100),
      };
    } else {
      // Zone cible (avec correction X/Y par commentaire pour le centre d'aide)
      if (!comment.zone_cible) return { x: 50, y: 50 };
      const ox = comment.zone_cible_offset_x ?? 0;
      const oy = comment.zone_cible_offset_y ?? 0;
      return {
        x: (comment.zone_cible.x + ox) + (comment.zone_cible.largeur * anchor.x / 100),
        y: (comment.zone_cible.y + oy) + (comment.zone_cible.hauteur * anchor.y / 100),
      };
    }
  }, []);

  return (
    <div 
      ref={containerRef}
      className="relative w-full h-full bg-black overflow-hidden"
    >
      {/* Lecteur YouTube */}
      <div className="absolute inset-0">
        <div 
          id={playerContainerId.current}
          className="absolute inset-0 w-full h-full"
        />
      </div>

      {/* Overlay Spotlight avec SVG mask (correction X/Y par commentaire) — actif dès qu'un commentaire est affiché si spotlight_on !== false */}
      {activeComment && activeComment.spotlight_on !== false && (() => {
        const opacity = (activeComment.spotlight_opacity ?? activeComment.zone_cible?.opacity ?? 100) / 100;
        if (!activeComment.zone_cible) {
          return (
            <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 10 }}>
              <rect x="0" y="0" width="100%" height="100%" fill={`rgba(0, 0, 0, ${opacity})`} />
            </svg>
          );
        }
        const ox = activeComment.zone_cible_offset_x ?? 0;
        const oy = activeComment.zone_cible_offset_y ?? 0;
        const zx = activeComment.zone_cible.x + ox;
        const zy = activeComment.zone_cible.y + oy;
        const zl = activeComment.zone_cible.largeur;
        const zh = activeComment.zone_cible.hauteur;
        return (
          <svg 
            className="absolute inset-0 w-full h-full pointer-events-none"
            style={{ zIndex: 10 }}
          >
            <defs>
              <mask id={`help-spotlight-mask-${activeComment.id}`}>
                <rect x="0" y="0" width="100%" height="100%" fill="white" />
                {(activeComment.zone_cible.forme || 'rectangle') === 'ellipse' ? (
                  <ellipse
                    cx={`${zx + zl / 2}%`}
                    cy={`${zy + zh / 2}%`}
                    rx={`${zl / 2}%`}
                    ry={`${zh / 2}%`}
                    fill="black"
                  />
                ) : (
                  <rect
                    x={`${zx}%`}
                    y={`${zy}%`}
                    width={`${zl}%`}
                    height={`${zh}%`}
                    fill="black"
                  />
                )}
              </mask>
            </defs>
            <rect
              x="0"
              y="0"
              width="100%"
              height="100%"
              fill={`rgba(0, 0, 0, ${opacity})`}
              mask={`url(#help-spotlight-mask-${activeComment.id})`}
            />
            {(activeComment.zone_cible.forme || 'rectangle') === 'ellipse' ? (
              <ellipse
                cx={`${zx + zl / 2}%`}
                cy={`${zy + zh / 2}%`}
                rx={`${zl / 2}%`}
                ry={`${zh / 2}%`}
                fill="none"
                stroke={COMMENT_STYLES[activeComment.type]?.hex || '#3b82f6'}
                strokeWidth="3"
              />
            ) : (
              <rect
                x={`${zx}%`}
                y={`${zy}%`}
                width={`${zl}%`}
                height={`${zh}%`}
                fill="none"
                stroke={COMMENT_STYLES[activeComment.type]?.hex || '#3b82f6'}
                strokeWidth="3"
              />
            )}
          </svg>
        );
      })()}

      {/* Ligne de liaison */}
      {activeComment?.zone_cible && activeComment?.afficher_liaison && (() => {
        const commentAnchor = getAnchorPosition(activeComment, 'comment', activeComment.ancre_commentaire);
        const spotlightAnchor = getAnchorPosition(activeComment, 'zone', activeComment.ancre_zone);
        return (
          <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 11 }}>
            <line
              x1={`${commentAnchor.x}%`}
              y1={`${commentAnchor.y}%`}
              x2={`${spotlightAnchor.x}%`}
              y2={`${spotlightAnchor.y}%`}
              stroke={COMMENT_STYLES[activeComment.type]?.hex || '#3b82f6'}
              strokeWidth="3"
              strokeDasharray="8,4"
            />
          </svg>
        );
      })()}

      {/* Bulle de commentaire */}
      {activeComment && (
        <div
          className={cn(
            'absolute z-20 p-4 rounded-xl shadow-2xl max-w-[320px] backdrop-blur-sm',
            commentBubbleFrameClasses(commentBubbleTheme, COMMENT_STYLES[activeComment.type] || COMMENT_STYLES.info)
          )}
          style={{
            left: `${activeComment.position_x}%`,
            top: `${activeComment.position_y}%`,
            transform: 'translate(-50%, -50%)',
          }}
        >
          <div className="min-w-0">
            <p className={cn('font-medium text-sm leading-relaxed', commentBubblePrimaryTextClass(commentBubbleTheme))}>
              {activeComment.texte}
            </p>
          </div>
          <div
            className={cn(
              'flex items-center gap-2 mt-3 pt-3 border-t',
              commentBubbleTheme === 'clair' ? 'border-gray-300' : 'border-gray-600'
            )}
          >
            <Button
              size="sm"
              onClick={continuePlayback}
              className={`flex-1 ${COMMENT_STYLES[activeComment.type]?.color || 'bg-blue-500'} hover:opacity-90 text-white`}
            >
              <ChevronRight size={16} className="mr-1" />
              Continuer
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={skipComment}
              className={
                commentBubbleTheme === 'clair'
                  ? 'text-gray-600 hover:text-gray-900'
                  : 'text-gray-400 hover:text-gray-200'
              }
            >
              <SkipForward size={16} />
            </Button>
          </div>
        </div>
      )}

      {/* Zone de contrôles masquée en centre d'aide : barre de temps, marqueurs et bouton play cachés pour éviter les bugs au clic */}

      {/* Loading */}
      {!playerReady && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-900 z-30">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-white/30 border-t-white"></div>
        </div>
      )}
    </div>
  );
};

// Composant pour le mode Étapes (images fixes)
export const StepsPlayer = forwardRef(function StepsPlayer(
  {
    tutorial,
    onComplete,
    onClose,
    onReachedLastStep,
    hideInternalNav = false,
    onNavStateChange,
    layoutCard = false,
    /** false = zone d’étapes plus haute (fenêtre dédiée) */
    compactLayout = true,
    /** Étape initiale (0-based), ex. depuis ?step=1 → 0 */
    initialStepIndex = 0,
    /** Fenêtre /aide/tutoriel/… : occupe la hauteur dispo, scroll interne, pied de page fixe sous la carte */
    fitViewport = false,
    /** Ne pas afficher le bloc « Étape / commentaire » dans la carte (affiché par le parent, ex. header page autonome) */
    externalizeStepMeta = false,
    /** Métadonnées d’étape pour affichage externe : { current, total, description } */
    onStepMetaChange = null,
  },
  ref
) {
  const playerRef = useRef(null);
  const playerContainerId = useRef(`youtube-steps-${Date.now()}`);
  const containerRef = useRef(null);
  const initialSeekDone = useRef(false);
  const initialStepIndexRef = useRef(initialStepIndex);
  const [playerReady, setPlayerReady] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  useEffect(() => {
    initialStepIndexRef.current = Number(initialStepIndex) || 0;
  }, [initialStepIndex]);

  useEffect(() => {
    initialSeekDone.current = false;
  }, [tutorial?.id]);

  const comments = tutorial?.commentaires || [];
  const isStepsMode = tutorial?.mode === 'etapes';
  const hasOrdre = comments.some((c) => c.ordre != null);

  // En mode étapes avec ordre : 1 commentaire = 1 étape, tri par ordre
  // Sinon : grouper par timestamp (< 0.3s d'écart = même étape)
  const steps = useMemo(() => {
    if (!comments.length) return [];

    const sorted = hasOrdre
      ? [...comments].sort((a, b) => (a.ordre ?? 999) - (b.ordre ?? 999))
      : [...comments].sort((a, b) => a.timestamp - b.timestamp);

    if (isStepsMode && hasOrdre) {
      return sorted.map((c) => ({
        timestamp: c.timestamp ?? 0,
        comments: [c],
      }));
    }

    const grouped = [];
    let currentGroup = [sorted[0]];
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i].timestamp - sorted[i - 1].timestamp < 0.3) {
        currentGroup.push(sorted[i]);
      } else {
        grouped.push({
          timestamp: currentGroup[0].timestamp,
          comments: currentGroup
        });
        currentGroup = [sorted[i]];
      }
    }
    grouped.push({
      timestamp: currentGroup[0].timestamp,
      comments: currentGroup
    });
    return grouped;
  }, [comments, isStepsMode, hasOrdre]);

  const currentStep = steps[currentStepIndex];
  const prevStepIndexRef = useRef(-1);

  useEffect(() => {
    prevStepIndexRef.current = -1;
  }, [tutorial?.id]);

  useEffect(() => {
    if (!steps.length) return;
    const lastIdx = steps.length - 1;
    if (currentStepIndex === lastIdx && prevStepIndexRef.current !== currentStepIndex) {
      onReachedLastStep?.();
    }
    prevStepIndexRef.current = currentStepIndex;
  }, [currentStepIndex, steps.length, onReachedLastStep, tutorial?.id]);

  // Extraire l'ID YouTube
  const getYoutubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
    return match ? match[1] : null;
  };

  const youtubeId = getYoutubeId(tutorial?.video_url);

  // Initialiser le player YouTube
  useEffect(() => {
    if (!youtubeId) return;

    const loadYoutubeAPI = () => {
      return new Promise((resolve) => {
        if (window.YT && window.YT.Player) {
          resolve();
          return;
        }

        // Callback quand l'API est prête
        const previousCallback = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = () => {
          if (previousCallback) previousCallback();
          resolve();
        };

        // Charger le script si pas déjà présent
        if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
          const tag = document.createElement('script');
          tag.src = 'https://www.youtube.com/iframe_api';
          document.head.appendChild(tag);
        } else if (window.YT && window.YT.Player) {
          // Script déjà chargé et prêt
          resolve();
        }
        // Si le script est chargé mais YT pas encore prêt, on attend le callback
      });
    };

    const initPlayer = async () => {
      await loadYoutubeAPI();
      // Attendre un peu pour s'assurer que l'API est bien initialisée
      await new Promise(resolve => setTimeout(resolve, 100));

      const container = document.getElementById(playerContainerId.current);
      if (!container || playerRef.current) return;

      playerRef.current = new window.YT.Player(playerContainerId.current, {
        videoId: youtubeId,
        width: '100%',
        height: '100%',
        playerVars: {
          autoplay: 0,
          controls: 0,
          modestbranding: 1,
          rel: 0,
          fs: 0,
          playsinline: 1,
          disablekb: 1,
        },
        events: {
          onReady: (event) => {
            setPlayerReady(true);
            // Démarrer brièvement la vidéo pour l'initialiser, puis pause et seek
            if (steps.length > 0 && playerRef.current) {
              // Muter et jouer brièvement pour initialiser
              playerRef.current.mute();
              playerRef.current.playVideo();
            }
          },
          onStateChange: (event) => {
            // Quand la vidéo commence à jouer, on fait le seek vers le premier timestamp
            if (event.data === window.YT.PlayerState.PLAYING && !initialSeekDone.current) {
              initialSeekDone.current = true;
              setTimeout(() => {
                if (playerRef.current && typeof playerRef.current.seekTo === 'function') {
                  const si = Math.min(
                    Math.max(0, Number(initialStepIndexRef.current) || 0),
                    Math.max(0, steps.length - 1)
                  );
                  playerRef.current.seekTo(steps[si]?.timestamp || 0, true);
                  playerRef.current.pauseVideo();
                  playerRef.current.unMute();
                  setCurrentStepIndex(si);
                }
              }, 100);
            }
          },
        },
      });
    };

    initPlayer();

    return () => {
      if (playerRef.current && playerRef.current.destroy) {
        try {
          playerRef.current.destroy();
        } catch (e) {}
        playerRef.current = null;
      }
    };
  }, [youtubeId, steps]);

  // Aller à une étape
  const goToStep = (index) => {
    if (index < 0 || index >= steps.length) return;
    setCurrentStepIndex(index);
    if (playerRef.current && playerReady && typeof playerRef.current.seekTo === 'function') {
      playerRef.current.seekTo(steps[index].timestamp, true);
      if (typeof playerRef.current.pauseVideo === 'function') {
        playerRef.current.pauseVideo();
      }
    }
  };

  // Mode captures sans vidéo YouTube : positionner l’étape initiale (ref = URL / fenêtre dédiée)
  useEffect(() => {
    if (youtubeId) return;
    if (!steps.length) return;
    const idx = Math.min(Math.max(0, Number(initialStepIndexRef.current) || 0), steps.length - 1);
    setCurrentStepIndex(idx);
  }, [youtubeId, steps, tutorial?.id]);

  // Étape suivante
  const nextStep = () => {
    if (currentStepIndex < steps.length - 1) {
      goToStep(currentStepIndex + 1);
    } else {
      onComplete?.();
    }
  };

  // Étape précédente
  const prevStep = () => {
    if (currentStepIndex > 0) {
      goToStep(currentStepIndex - 1);
    }
  };

  /* Toujours exposer les handlers à jour (pas de tableau de deps : évite fermetures stale). */
  useImperativeHandle(ref, () => ({
    prevStep,
    nextStep,
  }));

  useEffect(() => {
    if (!hideInternalNav || typeof onNavStateChange !== 'function') return;
    if (!steps.length) return;
    onNavStateChange({
      current: currentStepIndex + 1,
      total: steps.length,
      isLast: currentStepIndex >= steps.length - 1,
    });
  }, [hideInternalNav, onNavStateChange, currentStepIndex, steps.length]);

  const useScreenshot = stepHasUsableScreenshot(currentStep);

  const stepDescriptionText = useMemo(() => {
    if (!currentStep?.comments?.length) return '';
    return currentStep.comments
      .map((c) => (c.texte || '').trim())
      .filter(Boolean)
      .join('\n\n');
  }, [currentStep]);

  useEffect(() => {
    if (!layoutCard || !externalizeStepMeta || typeof onStepMetaChange !== 'function') return;
    if (!steps.length) return;
    onStepMetaChange({
      current: currentStepIndex + 1,
      total: steps.length,
      description: stepDescriptionText,
    });
  }, [
    layoutCard,
    externalizeStepMeta,
    onStepMetaChange,
    currentStepIndex,
    steps.length,
    stepDescriptionText,
  ]);

  if (steps.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        Aucune étape disponible
      </div>
    );
  }

  if (!layoutCard) {
    return (
      <div ref={containerRef} className="relative h-full w-full bg-black">
        <div className="absolute inset-0" style={{ visibility: useScreenshot ? 'hidden' : 'visible' }}>
          <div id={playerContainerId.current} className="absolute inset-0 h-full w-full" />
        </div>
        <TutorialStepStage
          tutorial={tutorial}
          currentStep={currentStep}
          currentStepIndex={currentStepIndex}
          mode={useScreenshot ? 'screenshot' : 'videoOverlays'}
          layout="inline"
          alt={`Étape ${currentStepIndex + 1}`}
        />
        {!hideInternalNav && (
          <div className="absolute bottom-0 left-0 right-0 z-30 bg-gradient-to-t from-black/80 to-transparent p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-white">
                Étape {currentStepIndex + 1} / {steps.length}
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={prevStep}
                  disabled={currentStepIndex === 0}
                  className="border-white/30 bg-white/20 text-white hover:bg-white/30 disabled:opacity-50"
                >
                  <ChevronRight size={16} className="mr-1 rotate-180" />
                  Précédent
                </Button>
                <Button size="sm" onClick={nextStep} className="bg-blue-600 text-white hover:bg-blue-700">
                  {currentStepIndex === steps.length - 1 ? 'Terminer' : 'Suivant'}
                  {currentStepIndex < steps.length - 1 && <ChevronRight size={16} className="ml-1" />}
                </Button>
              </div>
              <div className="flex gap-1">
                {steps.map((_, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => goToStep(idx)}
                    className={`h-2 w-2 rounded-full transition-colors ${
                      idx === currentStepIndex ? 'bg-blue-500' : 'bg-white/40 hover:bg-white/60'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  const layoutCardScrollClass =
    fitViewport && externalizeStepMeta
      ? 'flex min-h-0 flex-1 flex-col overflow-hidden'
      : fitViewport
        ? 'min-h-0 flex-1 overflow-y-auto'
        : compactLayout
          ? 'max-h-[40vh] overflow-auto'
          : 'min-h-0 flex-1 overflow-y-auto';

  const layoutCardRootClass = cn(
    'flex w-full flex-col overflow-hidden rounded-lg border border-border bg-card',
    !compactLayout && 'min-h-0 flex-1',
    fitViewport && 'h-full min-h-0 flex-1'
  );

  const showDescriptionInCard = layoutCard && !externalizeStepMeta;

  const descriptionBlock = (
    <div className={cn('p-4', fitViewport && 'border-t border-border/70 bg-card')}>
      <p className="text-xs text-muted-foreground">
        Étape {currentStepIndex + 1} / {steps.length}
      </p>
      {stepDescriptionText ? (
        <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-foreground">
          {stepDescriptionText}
        </p>
      ) : null}
    </div>
  );

  return (
    <div className={layoutCardRootClass}>
      <div className={cn('relative w-full bg-muted/50', layoutCardScrollClass)}>
        <div
          ref={containerRef}
          className={cn(
            'relative w-full',
            fitViewport && externalizeStepMeta && 'flex min-h-0 flex-1 flex-col'
          )}
        >
          <div className={cn('relative w-full', useScreenshot && 'hidden')}>
            <div
              className={cn(
                'relative aspect-video w-full overflow-hidden',
                compactLayout && !fitViewport && 'max-h-[40vh]',
                !compactLayout && !fitViewport && 'min-h-[200px] flex-1',
                fitViewport && !externalizeStepMeta && 'max-h-[min(42dvh,50vh)]',
                fitViewport && externalizeStepMeta && 'max-h-none min-h-0 flex-1'
              )}
            >
              <div id={playerContainerId.current} className="absolute inset-0 h-full w-full" />
            </div>
          </div>
          <div
            className={cn(
              'relative w-full',
              fitViewport && externalizeStepMeta && 'flex min-h-0 flex-1 flex-col'
            )}
          >
            <TutorialStepStage
              tutorial={tutorial}
              currentStep={currentStep}
              currentStepIndex={currentStepIndex}
              mode={useScreenshot ? 'screenshot' : 'videoOverlays'}
              layout="layoutCard"
              fitViewport={fitViewport}
              externalizeStepMeta={externalizeStepMeta}
              compactLayout={compactLayout}
              alt={`Étape ${currentStepIndex + 1} / ${steps.length}`}
            />
          </div>
        </div>
        {fitViewport && showDescriptionInCard ? descriptionBlock : null}
      </div>
      {!fitViewport && showDescriptionInCard ? descriptionBlock : null}
    </div>
  );
});

// Composant principal HelpPanel
const HelpPanel = () => {
  const {
    isHelpPanelOpen,
    helpAudience,
    closeHelpPanel,
    getVisibleThemes,
    getTutorialsByTheme,
    isTutorialViewed,
    markAsViewed,
    getTutorialHelpBadge,
    playTutorial,
    currentVideo,
    closeVideoPlayer,
    playlist,
    playlistIndex,
    nextInPlaylist,
  } = useVideoTutorial();

  const [expandedThemes, setExpandedThemes] = useState(new Set());
  const [initialized, setInitialized] = useState(false);
  const [sidebarSearch, setSidebarSearch] = useState('');
  
  // Mémoïser les thèmes visibles pour éviter les re-renders infinis
  const visibleThemes = useMemo(() => getVisibleThemes(), [getVisibleThemes]);
  const themeIds = useMemo(() => visibleThemes.map(t => t.id).join(','), [visibleThemes]);

  // Thèmes repliés à l’ouverture ; réinitialiser à la fermeture
  useEffect(() => {
    if (isHelpPanelOpen && !initialized && visibleThemes.length > 0) {
      setExpandedThemes(new Set());
      setInitialized(true);
    }
    if (!isHelpPanelOpen) {
      setInitialized(false);
      setExpandedThemes(new Set());
      setSidebarSearch('');
    }
  }, [isHelpPanelOpen, initialized, themeIds, visibleThemes]);

  const stepsPlayerRef = useRef(null);
  const [stepNavInfo, setStepNavInfo] = useState({ current: 1, total: 1, isLast: true });

  useEffect(() => {
    if (!currentVideo) {
      setStepNavInfo({ current: 1, total: 1, isLast: true });
      return;
    }
    if (isStepsLikeTutorial(currentVideo)) return;
    if (playlist?.length > 1) {
      setStepNavInfo({
        current: playlistIndex + 1,
        total: playlist.length,
        isLast: playlistIndex >= playlist.length - 1,
      });
    } else {
      setStepNavInfo({ current: 1, total: 1, isLast: false });
    }
  }, [currentVideo, playlist, playlistIndex]);

  const currentThemeLabel = useMemo(() => {
    if (!currentVideo?.theme_id) return '';
    const t = visibleThemes.find((th) => th.id === currentVideo.theme_id);
    return t?.nom || '';
  }, [currentVideo, visibleThemes]);

  /** Thèmes + tutoriels (filtrés par titre / description si recherche) */
  const filteredSidebarThemes = useMemo(() => {
    const q = sidebarSearch.trim().toLowerCase();
    return visibleThemes
      .map((theme) => {
        const all = getTutorialsByTheme(theme.id);
        const tutorials = q
          ? all.filter((t) => {
              const title = (t.titre || '').toLowerCase();
              const desc = (t.description || '').toLowerCase();
              return title.includes(q) || desc.includes(q);
            })
          : all;
        return { theme, tutorials };
      })
      .filter((row) => row.tutorials.length > 0);
  }, [visibleThemes, getTutorialsByTheme, sidebarSearch]);

  // Gérer le bouton retour du navigateur
  useEffect(() => {
    if (!isHelpPanelOpen) return;

    // Ajouter une entrée dans l'historique quand le panel s'ouvre
    window.history.pushState({ helpPanel: true }, '');

    const handlePopState = (event) => {
      // Fermer le panel quand l'utilisateur clique sur le bouton retour
      if (currentVideo) {
        closeVideoPlayer();
        // Remettre une entrée pour permettre de fermer le panel ensuite
        window.history.pushState({ helpPanel: true }, '');
      } else {
        closeHelpPanel();
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isHelpPanelOpen, currentVideo, closeVideoPlayer, closeHelpPanel]);

  // Fonction pour fermer et retourner à l'application
  const handleClose = useCallback(() => {
    if (currentVideo) closeVideoPlayer();
    closeHelpPanel();
    if (window.history.state?.helpPanel) {
      window.history.back();
    }
  }, [currentVideo, closeVideoPlayer, closeHelpPanel]);

  const toggleTheme = (themeId) => {
    setExpandedThemes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(themeId)) {
        newSet.delete(themeId);
      } else {
        newSet.add(themeId);
      }
      return newSet;
    });
  };

  const handleTutorialClick = (tutorial) => {
    if (isLinkTutorial(tutorial)) {
      const href = getTutorialExternalLink(tutorial);
      if (!href) return;
      window.open(href, '_blank', 'noopener,noreferrer');
      markAsViewed(tutorial.id);
      return;
    }
    playTutorial(tutorial);
  };

  const handleVideoComplete = () => {
    if (currentVideo) {
      markAsViewed(currentVideo.id);
    }

    if (playlist) {
      const hasNext = nextInPlaylist();
      if (!hasNext) {
        closeVideoPlayer();
      }
    } else {
      closeVideoPlayer();
    }
  };

  const footerPrevDisabled =
    !currentVideo ||
    !isStepsLikeTutorial(currentVideo) ||
    (stepNavInfo?.current ?? 1) <= 1;

  const handleFooterPrev = () => {
    if (!currentVideo || !isStepsLikeTutorial(currentVideo)) return;
    stepsPlayerRef.current?.prevStep?.();
  };

  const handleFooterNext = () => {
    if (!currentVideo) return;
    if (isStepsLikeTutorial(currentVideo)) {
      stepsPlayerRef.current?.nextStep?.();
      return;
    }
    if (playlist && playlist.length > 1 && playlistIndex < playlist.length - 1) {
      nextInPlaylist();
      return;
    }
    handleVideoComplete();
  };

  const openTutorialInNewWindow = useCallback(() => {
    if (!currentVideo?.id) return;
    const step =
      isStepsLikeTutorial(currentVideo) && stepNavInfo?.current
        ? stepNavInfo.current
        : 1;
    const href = resolveTutorialFullViewOpenUrl({
      fullViewUrl: currentVideo.full_view_url,
      tutorialId: currentVideo.id,
      step,
    });
    window.open(href, '_blank', 'noopener,noreferrer');
  }, [currentVideo, stepNavInfo]);

  const helpPanelTutorialNavRef = useRef({
    handleFooterPrev,
    handleFooterNext,
    footerPrevDisabled,
  });
  helpPanelTutorialNavRef.current = {
    handleFooterPrev,
    handleFooterNext,
    footerPrevDisabled,
  };

  useEffect(() => {
    if (!isHelpPanelOpen || !currentVideo) return;

    const onKeyDown = (e) => {
      const el = e.target;
      if (
        el &&
        (el.tagName === 'INPUT' ||
          el.tagName === 'TEXTAREA' ||
          el.tagName === 'SELECT' ||
          el.isContentEditable)
      ) {
        return;
      }
      if (e.key === 'ArrowLeft') {
        const { handleFooterPrev: prev, footerPrevDisabled: prevDisabled } =
          helpPanelTutorialNavRef.current;
        if (prevDisabled) return;
        e.preventDefault();
        prev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        helpPanelTutorialNavRef.current.handleFooterNext();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isHelpPanelOpen, currentVideo]);

  if (!isHelpPanelOpen) return null;

  const renderTutorialFooterBar = () => {
    if (!currentVideo) return null;
    const isSteps = isStepsLikeTutorial(currentVideo);
    return (
      <div
        className="grid w-full shrink-0 grid-cols-3 items-center gap-2 border-t border-border bg-background py-3"
        data-testid="help-tutorial-footer"
      >
        <div className="min-w-0 justify-self-start">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={footerPrevDisabled}
            onClick={handleFooterPrev}
            className="border-border bg-transparent text-foreground hover:bg-accent disabled:opacity-40"
          >
            <ChevronRight size={14} className="mr-1 rotate-180" />
            Précédent
          </Button>
        </div>
        <div className="justify-self-center">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={openTutorialInNewWindow}
            className="shrink-0 border-border bg-transparent text-foreground hover:bg-accent"
            title="Ouvrir dans une nouvelle fenêtre (ex. 2e écran)"
            aria-label="Agrandir : ouvrir dans une nouvelle fenêtre"
          >
            Agrandir
          </Button>
        </div>
        <div className="flex min-w-0 justify-end justify-self-end">
          {isSteps ? (
            <Button
              type="button"
              size="sm"
              onClick={handleFooterNext}
              className={
                stepNavInfo.isLast
                  ? 'bg-primary font-semibold text-primary-foreground hover:bg-primary/90'
                  : 'bg-primary font-medium text-primary-foreground hover:bg-primary/90'
              }
            >
              {stepNavInfo.isLast ? (
                'Parcours terminé'
              ) : (
                <>
                  Suivant
                  <ChevronRight size={16} className="ml-1" />
                </>
              )}
            </Button>
          ) : playlist && playlist.length > 1 && playlistIndex < playlist.length - 1 ? (
            <Button
              type="button"
              size="sm"
              onClick={handleFooterNext}
              className="bg-primary font-medium text-primary-foreground hover:bg-primary/90"
            >
              Suivant
              <ChevronRight size={16} className="ml-1" />
            </Button>
          ) : (
            <Button
              type="button"
              size="sm"
              onClick={handleFooterNext}
              className="bg-primary font-semibold text-primary-foreground hover:bg-primary/90"
            >
              Parcours terminé
            </Button>
          )}
        </div>
      </div>
    );
  };

  /* ~20–26 % du viewport sur grands écrans (réf. Annuaire), sans plafond trop bas */
  const sidebarShell =
    'pointer-events-auto absolute top-0 right-0 flex h-full w-[min(25vw,540px)] min-w-[300px] max-w-[100vw] flex-col overflow-hidden rounded-t-2xl border-l border-border bg-background text-foreground shadow-[-12px_0_32px_rgba(0,0,0,0.45)] dark:shadow-[-12px_0_32px_rgba(0,0,0,0.55)]';

  return createPortal(
    <div className="fixed inset-0 z-[45] pointer-events-none" data-testid="help-panel-root">
      <aside className={sidebarShell} data-testid="help-panel-sidebar">
        {!currentVideo ? (
          <>
            <header className="relative flex shrink-0 flex-col border-b border-border bg-background pl-6 pr-14 pt-6 pb-2">
              <button
                type="button"
                onClick={handleClose}
                className="absolute right-4 top-4 z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-border text-muted-foreground transition-colors hover:border-muted-foreground/70 hover:bg-accent hover:text-foreground"
                data-testid="help-panel-close-btn"
                aria-label="Fermer"
              >
                <X size={18} strokeWidth={2} />
              </button>
              <div className="flex items-start gap-3">
                <GraduationCap
                  className="h-5 w-5 shrink-0 text-muted-foreground"
                  strokeWidth={1.5}
                  aria-hidden
                />
                <div className="flex min-w-0 flex-1 flex-col space-y-1">
                  <h2 className="text-lg font-semibold leading-snug text-foreground">
                    Centre d&apos;aide
                  </h2>
                  <p className="text-sm font-normal leading-normal text-muted-foreground">
                    {helpAudience === 'admin' ? "Prysm'App — Administration" : "Prysm'App"}
                  </p>
                </div>
              </div>
            </header>

            <div className="min-h-0 flex-1 overflow-y-auto">
              {visibleThemes.length === 0 ? (
                <div className="px-6 py-10 text-center">
                  <Video size={40} className="mx-auto mb-3 text-muted-foreground" />
                  <p className="text-sm font-medium text-foreground">Aucun tutoriel disponible</p>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Il n&apos;y a pas encore de tutoriel pour cette application.
                  </p>
                </div>
              ) : (
                <div className="w-full">
                  <div className="sticky top-0 z-[1] border-b border-border bg-background px-6 py-3">
                    <label htmlFor="help-sidebar-tutorial-search" className="sr-only">
                      Rechercher un tutoriel par titre ou description
                    </label>
                    <div className="relative">
                      <Search
                        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                        strokeWidth={2}
                        aria-hidden
                      />
                      <Input
                        id="help-sidebar-tutorial-search"
                        type="search"
                        value={sidebarSearch}
                        onChange={(e) => setSidebarSearch(e.target.value)}
                        placeholder="Rechercher un tutoriel…"
                        className="h-9 border-border bg-background pl-9 text-sm text-foreground placeholder:text-muted-foreground"
                        autoComplete="off"
                        enterKeyHint="search"
                      />
                    </div>
                  </div>
                  <div className="w-full px-6 py-4">
                    {sidebarSearch.trim() && filteredSidebarThemes.length === 0 ? (
                      <p className="py-6 text-center text-sm text-muted-foreground">
                        Aucun tutoriel ne correspond à votre recherche.
                      </p>
                    ) : null}
                    {filteredSidebarThemes.map(({ theme, tutorials: themeTutorials }) => {
                    const hasSearch = Boolean(sidebarSearch.trim());
                    const isExpanded = hasSearch || expandedThemes.has(theme.id);
                    const themeHasFreshTutorial = themeTutorials.some((t) => {
                      const b = getTutorialHelpBadge(t);
                      return b === 'new' || b === 'updated';
                    });
                    const showThemeNouveauBadge = !isExpanded && themeHasFreshTutorial;

                    return (
                      <div key={theme.id} className="w-full border-b border-border">
                        <button
                          type="button"
                          className="flex w-full items-center justify-between gap-2 py-4 text-left transition-opacity hover:opacity-90"
                          onClick={() => toggleTheme(theme.id)}
                        >
                          <span className="min-w-0 flex-1 truncate text-left text-sm font-medium text-foreground">
                            {theme.nom}
                          </span>
                          <div className="flex shrink-0 items-center gap-2">
                            {showThemeNouveauBadge ? (
                              <Badge
                                variant="default"
                                className="shrink-0 gap-0.5 border-transparent bg-emerald-600 px-1.5 py-0 text-[10px] font-semibold text-primary-foreground shadow hover:bg-emerald-600"
                              >
                                <Sparkles className="h-2.5 w-2.5 shrink-0" strokeWidth={2} aria-hidden />
                                Nouveau
                              </Badge>
                            ) : null}
                            <span className="text-muted-foreground" aria-hidden>
                              {isExpanded ? (
                                <ChevronUp className="h-4 w-4" strokeWidth={2} />
                              ) : (
                                <ChevronDown className="h-4 w-4" strokeWidth={2} />
                              )}
                            </span>
                          </div>
                        </button>

                        {isExpanded && (
                          <div className="bg-background pb-4 pt-0">
                            <div className="space-y-2 pl-1">
                            {themeTutorials.map((tutorial) => {
                              const isViewed = isTutorialViewed(tutorial.id);
                              const helpBadge = getTutorialHelpBadge(tutorial);
                              const StepIcon =
                                tutorial.mode === 'lien'
                                  ? ExternalLink
                                  : tutorial.mode === 'etapes' || !tutorial.video_url
                                    ? Layers
                                    : Sun;

                              return (
                                <div
                                  key={tutorial.id}
                                  role="button"
                                  tabIndex={0}
                                  className="cursor-pointer rounded-lg border border-border bg-background p-3 transition-colors hover:border-border hover:bg-accent"
                                  onClick={() => handleTutorialClick(tutorial)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      handleTutorialClick(tutorial);
                                    }
                                  }}
                                >
                                  <div className="flex gap-3">
                                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent text-accent-foreground">
                                      <StepIcon className="h-4 w-4" strokeWidth={2} aria-hidden />
                                    </div>
                                    <div className="min-w-0 flex-1 space-y-1">
                                      <div className="flex items-start justify-between gap-2">
                                        <span className="min-w-0 text-sm font-medium text-foreground">
                                          {tutorial.titre}
                                        </span>
                                        <div className="flex flex-wrap items-start justify-end gap-1.5">
                                          {helpBadge === 'new' ? (
                                            <Badge
                                              variant="default"
                                              className="gap-0.5 border-transparent bg-emerald-600 px-1.5 py-0 text-[10px] font-semibold text-primary-foreground shadow hover:bg-emerald-600"
                                            >
                                              <Sparkles className="h-2.5 w-2.5 shrink-0" strokeWidth={2} aria-hidden />
                                              Nouveau
                                            </Badge>
                                          ) : null}
                                          {helpBadge === 'updated' ? (
                                            <Badge
                                              variant="secondary"
                                              className="gap-0.5 border-transparent bg-amber-500/90 px-1.5 py-0 text-[10px] font-semibold text-white shadow-none hover:bg-amber-500/90"
                                            >
                                              <RefreshCw className="h-2.5 w-2.5 shrink-0" strokeWidth={2} aria-hidden />
                                              Mis à jour
                                            </Badge>
                                          ) : null}
                                        </div>
                                      </div>
                                      {tutorial.description ? (
                                        <p className="text-xs font-normal leading-snug text-muted-foreground">
                                          {tutorial.description}
                                        </p>
                                      ) : null}
                                      {isViewed ? (
                                        <p className="mt-2 text-[11px] font-normal text-emerald-600 dark:text-emerald-400">
                                          Parcours terminé
                                        </p>
                                      ) : null}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  </div>
                </div>
              )}
            </div>
          </>
        ) : (
          <div
            className="flex min-h-0 flex-1 flex-col bg-background"
            data-testid="help-tutorial-stage"
          >
            <header className="shrink-0 border-b border-border bg-background px-6 pt-6 pb-2">
              <div className="flex items-center justify-between gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={closeVideoPlayer}
                  className="h-8 gap-1 px-2 text-muted-foreground hover:bg-accent hover:text-foreground"
                  data-testid="help-panel-back-btn"
                >
                  <ArrowLeft size={16} />
                  Retour
                </Button>
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-border text-muted-foreground transition-colors hover:border-muted-foreground/70 hover:bg-accent hover:text-foreground"
                  data-testid="help-panel-close-from-tutorial-btn"
                  aria-label="Fermer"
                >
                  <X size={18} strokeWidth={2} />
                </button>
              </div>
              <div className="mt-4 space-y-1">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2">
                    <h2 className="truncate text-lg font-semibold text-foreground">{currentVideo.titre}</h2>
                    <Eye size={16} className="flex-shrink-0 text-muted-foreground" aria-hidden />
                  </div>
                  {isTutorialViewed(currentVideo.id) ? (
                    <span className="shrink-0 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                      Parcours terminé
                    </span>
                  ) : null}
                </div>
                {currentThemeLabel ? (
                  <p className="text-sm font-normal text-muted-foreground">Thème : {currentThemeLabel}</p>
                ) : null}
                {currentVideo.description ? (
                  <p className="text-sm font-normal leading-snug text-muted-foreground">{currentVideo.description}</p>
                ) : null}
                {playlist && playlist.length > 1 ? (
                  <p className="text-xs font-normal text-muted-foreground">
                    Vidéo {playlistIndex + 1} sur {playlist.length}
                  </p>
                ) : null}
                {!isStepsLikeTutorial(currentVideo) && playlist && playlist.length > 1 ? (
                  <p className="text-xs font-normal text-muted-foreground">
                    Étape {stepNavInfo.current} / {stepNavInfo.total}
                  </p>
                ) : null}
              </div>
            </header>

            <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
              {isStepsLikeTutorial(currentVideo) ? (
                <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-4 pt-2">
                  <div className="flex flex-col space-y-4">
                    <div>
                      <StepsPlayer
                        ref={stepsPlayerRef}
                        hideInternalNav
                        layoutCard
                        compactLayout
                        onNavStateChange={setStepNavInfo}
                        tutorial={currentVideo}
                        onComplete={handleVideoComplete}
                        onClose={closeVideoPlayer}
                        onReachedLastStep={() => currentVideo?.id && markAsViewed(currentVideo.id)}
                      />
                    </div>
                    {renderTutorialFooterBar()}
                  </div>
                </div>
              ) : (
                <>
                  <div className="relative min-h-0 flex-1 bg-black">
                    <div className="relative h-full min-h-[220px] w-full">
                      <div className="relative h-full w-full">
                        <YouTubePlayerWithComments
                          tutorial={currentVideo}
                          onComplete={handleVideoComplete}
                          onClose={closeVideoPlayer}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="shrink-0 px-6">{renderTutorialFooterBar()}</div>
                </>
              )}
            </div>
          </div>
        )}
      </aside>
    </div>,
    document.body
  );
};

export default HelpPanel;
